import { httpRequest } from "@/lib";
import { APP_CONSTANTS } from "@/config";
import { useHitlStore } from "@/stores/use-hitl-store";
import type {
  HitlRequest,
  HitlResolver,
  HitlRiskLevel,
  HitlState,
  HitlTimeoutAction,
} from "@/components/generative-ui";

/**
 * Backend HITLRequest row as served by `GET /api/v1/session/hitl/requests`
 * (`HITLRequest::toInboxArray`).
 *
 * SCRUM-167: this projection and the `hitl.request.created` broadcast carry
 * the same decision fields — `HitlRequestPayloadParityTest` pins them against
 * each other — so a reviewer sees the same card whichever transport delivered
 * it. Fields stay optional because a local emitter or an older deployment may
 * send less; {@link apiRowToHitl} then leaves them undefined instead of
 * inventing a value.
 */
export interface ApiHitlRow {
  id: string;
  tenant_id?: string | null;
  state: HitlState;
  required_role?: string | null;
  assigned_user_id?: string | null;
  action_name?: string | null;
  action_description?: string | null;
  action_args_preview?: Record<string, unknown> | null;
  risk_level?: HitlRiskLevel | null;
  reversible?: boolean;
  agent_reasoning?: string | null;
  context_summary?: string | null;
  /** SCRUM-242 — the clinic's name for the agent that asked, when it has one. */
  agent_name?: string | null;
  on_timeout?: HitlTimeoutAction | null;
  created_at: string;
  expires_at: string;
  langgraph_run_id?: string | null;
  correlation_id?: string | null;
  /** SCRUM-191 — null while the request is pending. */
  resolved_at?: string | null;
  resolved_by?: HitlResolver | null;
}

export function apiRowToHitl(row: ApiHitlRow, tenantId?: string | null): HitlRequest {
  // Coarse mapping — agentType is derived from action_name, which is a tool
  // identifier and never reaches the screen (see hitl-card-copy.ts).
  const action = row.action_name ?? "";
  const agentType: HitlRequest["agentType"] =
    action.startsWith("saleops_") ? "growth"
    : action.startsWith("sdr_") ? "sdr"
    : action.startsWith("scheduler_") || action.includes("confirmation") ? "scheduler"
    : action.startsWith("postcare_") ? "postcare"
    : "copilot";
  const urgency: HitlRequest["urgency"] =
    row.risk_level === "high" || row.risk_level === "critical" ? "high"
    : row.risk_level === "low" ? "low"
    : "normal";
  return {
    id: row.id,
    tenantId: row.tenant_id ?? tenantId ?? "",
    agentType,
    toolName: action,
    payload: row.action_args_preview ?? {},
    createdAt: row.created_at,
    deadline: row.expires_at,
    urgency,
    conversationId: row.correlation_id ?? null,
    status: row.state,
    riskLevel: row.risk_level ?? undefined,
    reversible: row.reversible,
    agentReasoning: row.agent_reasoning ?? null,
    contextSummary: row.context_summary ?? null,
    agentName: row.agent_name ?? null,
    actionDescription: row.action_description ?? null,
    requiredRole: row.required_role ?? null,
    assignedUserId: row.assigned_user_id ?? null,
    runId: row.langgraph_run_id ?? null,
    onTimeout: row.on_timeout ?? null,
    resolvedAt: row.resolved_at ?? null,
    resolvedBy: row.resolved_by ?? null,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function isRiskLevel(value: string | null): value is HitlRiskLevel {
  return value === "low" || value === "medium" || value === "high" || value === "critical";
}

function isTimeoutAction(value: string | null): value is HitlTimeoutAction {
  return value === "auto_reject" || value === "notify" || value === "escalate";
}

/**
 * Maps the `hitl.request.created` broadcast to {@link ApiHitlRow}.
 *
 * Laravel publishes the ADR envelope (`HITLService::envelope`), not the inbox
 * row: the request lives under `card.payload`, with the action fields nested
 * one level deeper under `card.payload.action`. A bare row is still accepted
 * because local emitters send one.
 *
 * Returns null when neither shape is recognised — the caller must surface
 * that, never drop the request silently.
 */
export function createdEventToRow(payload: unknown): ApiHitlRow | null {
  const event = asRecord(payload);
  if (!event) return null;

  // Bare row (id + expires_at at the top level).
  if (asString(event.id) && asString(event.expires_at)) {
    return event as unknown as ApiHitlRow;
  }

  const card = asRecord(event.card);
  const cardPayload = asRecord(card?.payload);
  const requestId = asString(cardPayload?.request_id);
  if (!cardPayload || !requestId) return null;

  const action = asRecord(cardPayload.action) ?? {};
  const metadata = asRecord(event.metadata) ?? {};
  const riskLevel = asString(action.risk_level);
  const onTimeout = asString(cardPayload.on_timeout);

  return {
    id: requestId,
    tenant_id: asString(event.tenant_id) ?? asString(card?.tenant_id),
    state: "pending",
    required_role: asString(cardPayload.required_role),
    assigned_user_id: asString(cardPayload.assigned_user_id),
    action_name: asString(action.action_name),
    action_description: asString(action.description),
    action_args_preview: asRecord(action.args_preview),
    risk_level: isRiskLevel(riskLevel) ? riskLevel : null,
    reversible: typeof action.reversible === "boolean" ? action.reversible : undefined,
    agent_reasoning: asString(cardPayload.agent_reasoning),
    context_summary: asString(cardPayload.context_summary),
    // SCRUM-242 — same field, same level as the rest of the request payload;
    // the parity test pins it against the REST row.
    agent_name: asString(cardPayload.agent_name),
    on_timeout: isTimeoutAction(onTimeout) ? onTimeout : null,
    created_at: asString(card?.created_at) ?? new Date().toISOString(),
    expires_at: asString(cardPayload.expires_at) ?? new Date().toISOString(),
    langgraph_run_id: asString(metadata.langgraph_run_id),
    correlation_id: asString(event.correlation_id) ?? asString(card?.correlation_id),
  };
}

export const HITL_LOAD_ERROR = "Não foi possível carregar as aprovações pendentes.";
export const HITL_REALTIME_ERROR = "Sem conexão em tempo real com as aprovações.";
export const HITL_RESOLVED_LOAD_ERROR = "Não foi possível carregar as decisões de hoje.";

/**
 * `{ data: ApiHitlRow[] }` is the contract; a bare array and a legacy
 * `{ items: [...] }` envelope are tolerated.
 */
function rowsOf(body: unknown): ApiHitlRow[] {
  const envelope = asRecord(body);
  if (Array.isArray(envelope?.data)) return envelope.data as ApiHitlRow[];
  if (Array.isArray(envelope?.items)) return envelope.items as ApiHitlRow[];
  return Array.isArray(body) ? (body as ApiHitlRow[]) : [];
}

function currentTenantId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
}

/**
 * Loads the pending requests into the store. Called on mount by
 * `HitlPusherBridge` and again by the inbox's "Tentar novamente".
 *
 * A failure is written to the store (`error`, `connected: false`) so the
 * inbox can say the approvals are unreachable instead of drawing an empty
 * state that reads as "nothing to decide" (PRODUCT_GOVERNANCE rule 8).
 */
export async function hydrateHitlRequests(tenantId?: string | null): Promise<void> {
  const tenant = tenantId ?? currentTenantId();
  const store = useHitlStore.getState();
  try {
    const res = await httpRequest.get("/session/hitl/requests", {
      params: { state: "pending" },
    });
    for (const row of rowsOf(res?.data)) {
      store.upsert(apiRowToHitl(row, tenant));
    }
    store.setError(null);
    store.setConnected(true);
  } catch (err) {
    console.error("[hitl] hydrate failed:", err);
    store.setError(HITL_LOAD_ERROR);
    store.setConnected(false);
  }
}

/**
 * One request this clinic already decided, as the resolved tab shows it.
 *
 * `decidedBy` is null when nobody decided — the timeout sweep closed the
 * request — and the tab says so rather than attributing it to a person.
 */
export interface ResolvedHitlEntry {
  request: HitlRequest;
  state: Exclude<HitlState, "pending">;
  decidedBy: string | null;
  decidedAt: string;
}

/**
 * The decisions of one clinic day, read from the API
 * (`GET /session/hitl/requests?state=resolved&date=`).
 *
 * SCRUM-191: the tab used to list only what the reviewer decided in the open
 * browser tab, because `toInboxArray` carried neither `resolved_at` nor
 * `resolved_by`. A reload emptied it, and an empty "Resolvidas hoje" reads as
 * "nobody decided anything today" — a claim the records contradict.
 *
 * Throws on failure: the caller shows that the day could not be loaded instead
 * of drawing an empty list (PRODUCT_GOVERNANCE rule 8).
 */
export async function fetchResolvedHitlRequests(
  date: string,
  tenantId?: string | null
): Promise<ResolvedHitlEntry[]> {
  const tenant = tenantId ?? currentTenantId();
  const res = await httpRequest.get("/session/hitl/requests", {
    params: { state: "resolved", date },
  });

  const entries: ResolvedHitlEntry[] = [];
  for (const row of rowsOf(res?.data)) {
    // A row with no `resolved_at` is not a decision of any day; the api does
    // not send one here, and inventing "now" would file it under today.
    if (row.state === "pending" || typeof row.resolved_at !== "string") continue;
    entries.push({
      request: apiRowToHitl(row, tenant),
      state: row.state,
      decidedBy: row.resolved_by?.name ?? null,
      decidedAt: row.resolved_at,
    });
  }
  return entries;
}

/** The request id a decision/timeout envelope names, whatever its shape. */
export function resolvedEventRequestId(payload: unknown): string | null {
  const p = payload as {
    id?: unknown;
    request_id?: unknown;
    metadata?: { request_id?: unknown };
    card?: { payload?: { request_id?: unknown } };
  } | null;
  const candidate =
    p?.id ?? p?.request_id ?? p?.card?.payload?.request_id ?? p?.metadata?.request_id;
  return typeof candidate === "string" && candidate !== "" ? candidate : null;
}
